import { ActionRequestStatus, ActionRequestType } from '@billing/validation';
import {
  Button,
  Chip,
  Divider,
  Flex,
  Loader,
  Modal,
  Stack,
  Text,
  Textarea,
  useComputedColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateActionRequest } from 'api/resource/actionManagement';
import { ActionRequest } from 'interfaces';
import { nowISO, toJSDate } from 'utils/dateUtils';
import { useState } from 'react';
import { useCurrentUserStore } from 'store';
import { v4 } from 'uuid';

interface ClaimActionRequestModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  claimID: string;
  providerID: string;
}

export const ClaimActionRequestModal: React.FC<
  ClaimActionRequestModalProps
> = ({ open, setOpen, claimID, providerID }) => {
  const computedColorScheme = useComputedColorScheme();
  const queryClient = useQueryClient();
  const theme = useMantineTheme();

  const createActionRequest = useCreateActionRequest();

  const { id: userID } = useCurrentUserStore();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [requestType, setRequestType] = useState<ActionRequestType>(
    ActionRequestType.INFORMATION
  );
  const [message, setMessage] = useState<string>('');

  const handleClose = () => {
    setRequestType(ActionRequestType.INFORMATION);
    setMessage('');
    setOpen(false);
  };

  const handleCreateRequest = async () => {
    try {
      setIsLoading(true);
      const newRequest: Partial<ActionRequest> = {
        id: v4(),
        status: ActionRequestStatus.REQUESTED,
        providerID: providerID,
        createdAt: toJSDate(nowISO()),
        claimID: claimID,
        parentRequestID: null,
        requestedBy: userID,
        type: requestType,
        message: message,
      };
      await createActionRequest.mutateAsync(newRequest);
      queryClient.invalidateQueries();
    } catch (error) {
      console.error('Error creating Action Request:', error);
    } finally {
      setIsLoading(false);
      handleClose();
    }
  };

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      closeOnEscape
      withCloseButton
      size="lg"
      centered
      radius="lg"
      overlayProps={{ blur: 4, opacity: 0.55 }}
      styles={{
        header: { paddingBottom: theme.spacing.xs },
        body: { paddingTop: theme.spacing.xs },
        content: {
          background:
            computedColorScheme === 'dark'
              ? theme.colors.dark[7]
              : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
          border: `1px solid ${
            computedColorScheme === 'dark'
              ? theme.colors.dark[5]
              : theme.colors.gray[2]
          }`,
        },
      }}
      title={
        <Stack gap={4}>
          <Text fw={700} size="lg" data-testid="claim-action-request-title">
            {'Create Claim Action Request'}
          </Text>
          <Text
            size="sm"
            c="dimmed"
            data-testid="claim-action-request-subtitle"
          >
            {'Choose one request type and include a short message.'}
          </Text>
        </Stack>
      }
    >
      {isLoading ? (
        <Flex justify="center" align="center">
          <Loader />
        </Flex>
      ) : (
        <Stack gap="md">
          <Stack gap="sm">
            <Text size="sm" fw={600} data-testid="claim-action-request-type">
              {'Request type'}
            </Text>
            <Chip.Group
              multiple={false}
              value={requestType}
              onChange={(value) => setRequestType(value as ActionRequestType)}
            >
              <Flex gap="sm" align="center" justify="center">
                <Chip
                  color="blue"
                  size="md"
                  value={ActionRequestType.INFORMATION}
                  data-testid="claim-action-request-type-information"
                >
                  {ActionRequestType.INFORMATION}
                </Chip>
                <Chip
                  color="blue"
                  size="md"
                  value={ActionRequestType.SUPPORTING_DOCUMENTS}
                  data-testid="claim-action-request-type-supporting"
                >
                  {ActionRequestType.SUPPORTING_DOCUMENTS.replace('_', ' ')}
                </Chip>
              </Flex>
            </Chip.Group>
          </Stack>

          <Textarea
            label="Message"
            placeholder="Enter a message for the provider here"
            value={message}
            onChange={(event) => setMessage(event.currentTarget.value)}
            data-testid="claim-action-request-message"
            autosize
            minRows={4}
            radius="md"
          />
          <Divider />
          <Flex gap="sm" align="center" justify="center" mt="md">
            <Button
              variant="outline"
              color="grey"
              c={computedColorScheme === 'dark' ? 'white' : 'black'}
              size="md"
              data-testid="claim-action-request-cancel"
              onClick={handleClose}
            >
              {'Cancel'}
            </Button>
            <Button
              color="green"
              c={computedColorScheme === 'dark' ? 'white' : 'black'}
              size="md"
              disabled={!requestType}
              data-testid="claim-action-request-submit"
              onClick={handleCreateRequest}
            >
              {'Submit Request'}
            </Button>
          </Flex>
        </Stack>
      )}
    </Modal>
  );
};
